<template>
  <v-dialog
    v-if="showPhotoSelectionButton"
    v-model="isOpen"
    fullscreen
    transition="dialog-bottom-transition">
    <v-card>
      <v-card-title
        class="photo-selection-title">
        <v-toolbar
          class="photo-selection-title-bar"
          card
          dark
          color="#F7F7F7">
          <v-btn
            icon
            dark
            @click="closeDialog">
            <v-icon color="#686868">arrow_back_ios_new</v-icon>
          </v-btn>
          <v-toolbar-title class="black--text subheading">写真を選択</v-toolbar-title>
        </v-toolbar>
      </v-card-title>
      <v-container
        grid-list-lg>
        <v-layout
          row
          wrap>
          <v-flex
            xs12>
            <div class="photo-selection-description">
              ※画像の保管期間はAI検査日から6ヶ月です。
            </div>
          </v-flex>
          <v-flex
            v-if="!isAdditional"
            xs12
          >
            <div class="photo-selection-part">
              {{ label }}
            </div>
          </v-flex>
          <v-flex
            v-if="!hasPhotos && !isAdditional"
            xs12>
            写真が撮影されていないか、AI検査で適正な画像が選択されませんでした。<br>その他から選択いただくか、改めてご撮影下さい。
          </v-flex>
          <v-flex
            v-for="photo in mainPhotos"
            v-else
            :key="photo.imageId"
            xs4
          >
            <v-card
              :class="['photo-card', { selected: isSelected(photo) }]"
              flat
              tile
              @click="selectPhoto(photo)"
            >
              <v-img
                :class="[{ selected: isSelected(photo) }]"
                :src="getUrl(photo)"
                aspect-ratio="1.32"
                contain
              />
              <v-icon
                v-if="initSelectedPhotoNames.includes(photo.imageId)"
                color="red"
                class="top-left-icon">check_box
              </v-icon>
            </v-card>
          </v-flex>
          <v-flex
            v-if="!isAdditional"
            xs12
          >
            <div class="photo-selection-part">
              その他
            </div>
          </v-flex>
          <v-flex
            v-for="photo in otherPhotos"
            :key="photo.id"
            xs4>
            <v-card
              :class="['photo-card', { selected: isSelected(photo) }]"
              flat
              tile
              @click="selectPhoto(photo)"
            >
              <v-img
                :class="[ { selected: isSelected(photo) }]"
                :src="getUrl(photo)"
                aspect-ratio="1.32"
                contain
              />
              <v-icon
                v-if="initSelectedPhotoNames.includes(photo.imageId)"
                color="red"
                class="top-left-icon">check_box</v-icon>
            </v-card>
          </v-flex>

          <v-flex
            xs12
            class="floating">
            <v-btn
              :disabled="!selectedIds.length"
              color="primary"
              class="my-0"
              large
              block
              @click="select">
              決定
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card>
  </v-dialog>

</template>

<script>
export default {
  props: {
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    initSelected: {
      type: Array,
      default: () => [],
    },
    isDamageTab: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 1,
    },
    partId: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    selectedIds: [],
    mainPhotos: [],
    otherPhotos: [],
    initSelectedPhotoNames: [],
    submitLoading: false,
  }),
  computed: {
    selectedPhoto() {
      return [...this.mainPhotos, ...this.otherPhotos].filter(p => this.selectedIds.includes(p.id));
    },
    hasPhotos() {
      return this.mainPhotos.length > 0;
    },
    aiPhotos() {
      return this.$store.state.aiPhotos;
    },
    isOpen() {
      this.initProcess();
      return this.$store.state.showPhotoSelectionDialog;
    },
    showPhotoSelectionButton() {
      return this.$store.state.showPhotoSelectionButton;
    },
    isAdditional() {
      return this.name.includes('additional');
    },
  },
  methods: {
    initProcess() {
      if (this.isDamageTab) {
        this.mainPhotos = this.aiPhotos.filter(p => p.partId.includes(this.partId));
        this.otherPhotos = this.aiPhotos.filter(p => !p.partId.length && !!p.isDamage);
      } else {
        this.mainPhotos = this.aiPhotos.filter(p => p.type === `PHOTO_${this.name.toUpperCase()}`);
        this.otherPhotos = this.aiPhotos.filter(p => !p.type && p.imageTag !== 'Damage');
      }

      this.initSelectedPhotoNames = this.getInitSelectedPhotoName();
    },
    selectPhoto(photo) {
      const idx = this.selectedIds.findIndex(id => id === photo.id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
      } else {
        if (this.limit === this.selectedIds.length) {
          this.selectedIds.pop();
        }
        this.selectedIds.push(photo.id);
      }
    },
    closeDialog() {
      this.selectedIds = [];
      this.mainPhotos = [];
      this.otherPhotos = [];
      this.submitLoading = false;
      this.$store.commit('togglePhotoSelectionDialog');
    },
    async fetchPhoto(photo) {
      try {
        const response = await fetch(this.getUrl(photo), { cache: 'no-cache' });
        const imageBlob = await response.blob();

        return new File([imageBlob], photo.imageId, { type: 'image/jpeg' });
      } catch (error) {
        this.$store.commit('setError', {
          message: 'エラーが発生しました',
        });
      }
      return null;
    },
    async select() {
      if (!this.selectedPhoto || this.submitLoading) {
        return;
      }
      this.submitLoading = true;

      await Promise.all(this.selectedPhoto.map(async (photo) => {
        if (this.initSelectedPhotoNames.includes(photo.imageId)) {
          this.submitLoading = false;
          return;
        }

        const photoData = await this.fetchPhoto(photo);

        if (!photoData) {
          this.submitLoading = false;
          return;
        }
        this.$emit('upload', photoData);
      }));

      this.closeDialog();
    },
    isSelected(photo) {
      return this.selectedIds.includes(photo.id);
    },
    getInitSelectedPhotoName() {
      if (!this.initSelected || this.initSelected.length === 0) return [];

      if (this.isDamageTab) {
        return this.initSelected.map(i => i.cpFile.originalFile.originalName);
      }

      return this.initSelected;
    },
    getUrl(photo) {
      return this.isDamageTab ? photo.imageUrl : photo.originalImageUrl;
    },
  },
};
</script>

<style scoped>

.photo-selection-description {
  font-size: 12px;
  color: #686868;
  /* margin-bottom: 16px; */
}

.photo-selection-part {
  font-size: 18px;
  font-weight: bold;
  color: #333333;
  /* margin-bottom: 16px; */
}

.photo-card {
  position: relative; /* Ensure the card is positioned relative */
  border: 2px solid #D1D1D1;
  transition: none;
}
.photo-card.selected {
  border: 2px solid red;
}

.top-left-icon {
  position: absolute;
  top: 0px; /* Adjust as needed */
  left: 0px; /* Adjust as needed */
  padding: 4px;
}

.v-image.selected {
  outline: 3px solid red;
  outline-offset: -3px;
}

.photo-selection-title {
  padding: 0;
}

.photo-selection-title-bar >>> .v-toolbar__content{
  height: 40px !important;
}

.photo-selection-title .subheading {
  font-weight: bold;
  margin: 0 auto;
  position: relative;
  left: -18px;
}

.floating {
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.8);
}
</style>



// WEBPACK FOOTER //
// src/components/elements/PhotoSelection.vue